import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline subscribe widget for the Just Dive Weekly Ocean Report.
 * Two visual variants:
 *   - `hero` (default): big, photographic background, large heading. Use on
 *     the home page or near the bottom of a page.
 *   - `inline`: compact, single-row form. Use in the footer.
 */
const NewsletterSubscribe = ({ variant = 'hero' }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email || !email.includes('@')) {
      toast.error('Pop a valid email in there');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/newsletter-subscribers`, { email, first_name: firstName || null });
      setDone(true);
      toast.success("You're in — see you in your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sign-up failed, try again?');
    } finally {
      setSubmitting(false);
    }
  };

  if (variant === 'inline') {
    return done ? (
      <div className="flex items-center gap-2 text-sm text-[#FF6B35]" data-testid="newsletter-subscribed-inline">
        <Check className="w-4 h-4" /> Subscribed. Welcome aboard.
      </div>
    ) : (
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 max-w-md" data-testid="newsletter-subscribe-inline">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          className="flex-1 px-3 py-2 rounded bg-white/10 text-white placeholder-white/60 border border-white/20 focus:outline-none focus:ring-2 focus:ring-[#FF6B35] text-sm"
          data-testid="newsletter-email-inline"
        />
        <button
          type="submit"
          disabled={submitting}
          className="bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white font-semibold px-4 py-2 rounded text-sm flex items-center justify-center"
          data-testid="newsletter-submit-inline"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe'}
        </button>
      </form>
    );
  }

  // Hero variant
  return (
    <section
      className="relative overflow-hidden rounded-2xl my-12"
      style={{
        backgroundImage: "linear-gradient(rgba(10,35,66,0.78), rgba(10,35,66,0.78)), url('https://images.unsplash.com/photo-1582967788606-a171c1080cb0?w=1600&q=80&auto=format')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      data-testid="newsletter-subscribe-hero"
    >
      <div className="px-6 sm:px-12 py-12 sm:py-16 text-center max-w-3xl mx-auto">
        <div className="inline-block bg-[#FF6B35] text-white text-xs uppercase tracking-widest font-semibold px-3 py-1 rounded-full mb-4">
          Weekly Ocean Report
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-3">
          Salt in your inbox every Monday.
        </h2>
        <p className="text-white/80 text-base sm:text-lg mb-7 max-w-xl mx-auto">
          A premium diving magazine from Just Dive — real stories, real reefs, real gear,
          straight from passionate divers. No spam, just the good stuff.
        </p>
        {done ? (
          <div className="inline-flex items-center gap-2 text-white text-lg" data-testid="newsletter-subscribed-hero">
            <Check className="w-5 h-5 text-[#FF6B35]" /> You&apos;re in. See you in your inbox.
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name (optional)"
              className="sm:w-44 px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/50 border border-white/20 focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
              data-testid="newsletter-first-name-hero"
            />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="flex-1 px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/50 border border-white/20 focus:outline-none focus:ring-2 focus:ring-[#FF6B35]"
              data-testid="newsletter-email-hero"
            />
            <button
              type="submit"
              disabled={submitting}
              className="bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white font-semibold px-6 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
              data-testid="newsletter-submit-hero"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <>
                  <Mail className="w-5 h-5" /> Subscribe
                </>
              )}
            </button>
          </form>
        )}
        <p className="text-xs text-white/50 mt-4">
          Unsubscribe in one click any time. We never share your email.
        </p>
      </div>
    </section>
  );
};

export default NewsletterSubscribe;
